/**
 * localSettings.js — one-file helpers for every localStorage-backed
 * per-user setting and library on The Vaad Otzar. Grew out of the
 * Feb-2026 transcript-upgrades batch (quote journal, mark-as-studied,
 * hands-off scroll, high-contrast, sephardic transliteration).
 *
 * Everything is best-effort: if localStorage is unavailable (private
 * browsing, quota exceeded, etc.) the getters return the default and
 * setters swallow the error so the app never crashes.
 */

// ── Generic helpers ─────────────────────────────────────────────────
const safeGet = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v == null ? fallback : v;
  } catch { return fallback; }
};
const safeSet = (key, value) => {
  try { localStorage.setItem(key, value); } catch { /* noop */ }
};
const safeGetJSON = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v == null ? fallback : JSON.parse(v);
  } catch { return fallback; }
};
const safeSetJSON = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* noop */ }
};

// ── Quote journal (#1) ─────────────────────────────────────────────
// Every saved highlight lives under `vaad_quotes` as an array. Newest
// entries push to the front so /my-quotes shows them first.
const QUOTES_KEY = 'vaad_quotes';
export function loadQuotes() {
  const arr = safeGetJSON(QUOTES_KEY, []);
  return Array.isArray(arr) ? arr : [];
}
export function saveQuote(entry) {
  if (!entry || !entry.text) return null;
  const now = Date.now();
  const item = {
    id: entry.id || `q-${now}-${Math.random().toString(36).slice(2, 8)}`,
    text: entry.text,
    driveFileId: entry.driveFileId || '',
    shiurName: entry.shiurName || '',
    speaker: entry.speaker || '',
    wordIdx: Number.isFinite(entry.wordIdx) ? entry.wordIdx : null,
    startSec: Number.isFinite(entry.startSec) ? entry.startSec : null,
    savedAt: now,
  };
  const cur = loadQuotes().filter((q) => q.id !== item.id);
  cur.unshift(item);
  safeSetJSON(QUOTES_KEY, cur.slice(0, 500)); // cap at 500 to bound storage
  window.dispatchEvent(new CustomEvent('vaad-quotes-change'));
  return item;
}
export function removeQuote(id) {
  const next = loadQuotes().filter((q) => q.id !== id);
  safeSetJSON(QUOTES_KEY, next);
  window.dispatchEvent(new CustomEvent('vaad-quotes-change'));
  return next;
}
export function clearQuotes() {
  safeSetJSON(QUOTES_KEY, []);
  window.dispatchEvent(new CustomEvent('vaad-quotes-change'));
}

// ── Study progress (#2) ────────────────────────────────────────────
// { [driveFileId]: { studiedAt: timestamp } }
const STUDIED_KEY = 'vaad_studied';
export function loadStudied() {
  const obj = safeGetJSON(STUDIED_KEY, {});
  return obj && typeof obj === 'object' ? obj : {};
}
export function isStudied(driveFileId) {
  if (!driveFileId) return false;
  return Boolean(loadStudied()[driveFileId]);
}
export function toggleStudied(driveFileId) {
  if (!driveFileId) return false;
  const cur = loadStudied();
  if (cur[driveFileId]) {
    delete cur[driveFileId];
  } else {
    cur[driveFileId] = { studiedAt: Date.now() };
  }
  safeSetJSON(STUDIED_KEY, cur);
  window.dispatchEvent(new CustomEvent('vaad-studied-change'));
  return Boolean(cur[driveFileId]);
}

// ── Reading preferences (#25, #26, #29) ────────────────────────────
export const readPref = {
  get sephardic() { return safeGet('vaad_pron_sephardic', '0') === '1'; },
  set sephardic(v) {
    safeSet('vaad_pron_sephardic', v ? '1' : '0');
    window.dispatchEvent(new CustomEvent('vaad-readpref-change'));
  },
  get highContrast() { return safeGet('vaad_high_contrast', '0') === '1'; },
  set highContrast(v) {
    safeSet('vaad_high_contrast', v ? '1' : '0');
    applyHighContrast(v);
    window.dispatchEvent(new CustomEvent('vaad-readpref-change'));
  },
  get handsOff() { return safeGet('vaad_hands_off', '0') === '1'; },
  set handsOff(v) {
    safeSet('vaad_hands_off', v ? '1' : '0');
    window.dispatchEvent(new CustomEvent('vaad-readpref-change'));
  },
};

// Apply/remove the .vaad-high-contrast body class so global CSS can
// re-skin the whole app in one place.
export function applyHighContrast(enabled) {
  try {
    document.body.classList.toggle('vaad-high-contrast', !!enabled);
  } catch { /* noop */ }
}

// Boot-time hook: initialise body classes from stored prefs. Called by
// App.js so refreshing the page keeps the user's chosen mode.
export function initReadPrefs() {
  applyHighContrast(readPref.highContrast);
}
